import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getCertificationSnapshot,
  CertificationSnapshotResponse,
} from 'src/api/dataVault/historyCertifications';
import { scopeKey } from 'src/redux/slices/DataVault/dataVaultHistoryCertificationsSlice';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface DetailEntry {
  status: Status;
  data: CertificationSnapshotResponse | null;
  error: string | null;
  lastFetchedAt: number | null;
}

interface SliceState {
  byCertKey: Record<string, DetailEntry>;
}

const STALE_MS = 5 * 60 * 1000; // 5 minutes

const initialEntry: DetailEntry = {
  status: 'idle',
  data: null,
  error: null,
  lastFetchedAt: null,
};

const initialState: SliceState = { byCertKey: {} };

const certKey = (sk: string, certificationId: number): string =>
  `${sk}:${certificationId}`;

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

export const fetchCertificationSnapshot = createAsyncThunk<
  CertificationSnapshotResponse,
  {
    actingOnMemberId?: number | null;
    certificationId: number;
    forceRefresh?: boolean;
  },
  {
    state: { dataVaultHistoryCertificationDetail: SliceState };
    rejectValue: string;
  }
>(
  'dataVaultHistoryCertificationDetail/fetch',
  async ({ actingOnMemberId, certificationId }, { rejectWithValue }) => {
    try {
      return await getCertificationSnapshot(certificationId, {
        actingOnMemberId,
      });
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: string }; status?: number };
      };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load certification snapshot'
      );
    }
  },
  {
    condition: (
      { actingOnMemberId, certificationId, forceRefresh },
      { getState }
    ) => {
      if (forceRefresh) {
        return true;
      }
      const sk = scopeKey(actingOnMemberId);
      const key = certKey(sk, certificationId);
      const entry =
        getState().dataVaultHistoryCertificationDetail.byCertKey[key];
      if (!entry) {
        return true;
      }
      if (entry.status === 'loading') {
        return false;
      }
      if (
        entry.status === 'succeeded' &&
        entry.lastFetchedAt &&
        Date.now() - entry.lastFetchedAt < STALE_MS
      ) {
        return false;
      }
      return true;
    },
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'dataVaultHistoryCertificationDetail',
  initialState,
  reducers: {
    invalidateEntry(
      state,
      action: PayloadAction<{
        actingOnMemberId?: number | null;
        certificationId: number;
      }>
    ) {
      const sk = scopeKey(action.payload.actingOnMemberId);
      const key = certKey(sk, action.payload.certificationId);
      delete state.byCertKey[key];
    },
    clearAllForScope(state, action: PayloadAction<string>) {
      // Remove all entries that start with the given scopeKey prefix
      const prefix = `${action.payload}:`;
      for (const k of Object.keys(state.byCertKey)) {
        if (k.startsWith(prefix) || k === action.payload) {
          delete state.byCertKey[k];
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificationSnapshot.pending, (state, action) => {
        const sk = scopeKey(action.meta.arg.actingOnMemberId);
        const key = certKey(sk, action.meta.arg.certificationId);
        state.byCertKey[key] = { ...initialEntry, status: 'loading' };
      })
      .addCase(fetchCertificationSnapshot.fulfilled, (state, action) => {
        const sk = scopeKey(action.meta.arg.actingOnMemberId);
        const key = certKey(sk, action.meta.arg.certificationId);
        state.byCertKey[key] = {
          status: 'succeeded',
          data: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchCertificationSnapshot.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        const sk = scopeKey(action.meta.arg.actingOnMemberId);
        const key = certKey(sk, action.meta.arg.certificationId);
        state.byCertKey[key] = {
          ...initialEntry,
          status: 'failed',
          error:
            (action.payload as string | undefined) ??
            action.error.message ??
            'Failed',
        };
      });
  },
});

export const { invalidateEntry, clearAllForScope } = slice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCertificationSnapshot =
  (actingOnMemberId: number | null | undefined, certificationId: number) =>
  (state: { dataVaultHistoryCertificationDetail: SliceState }): DetailEntry => {
    const sk = scopeKey(actingOnMemberId);
    const key = certKey(sk, certificationId);
    return (
      state.dataVaultHistoryCertificationDetail.byCertKey[key] ?? initialEntry
    );
  };

export default slice.reducer;
